import { PROGRESS_REPORT_INTERVAL_BYTES } from '../../shared/constants.js';
import type { FileMeta, ReceiverControl } from '../../shared/transfer.js';
import { parseSenderControl, shouldReportProgress } from '../../shared/transfer.js';
import { PeerConnectionError, TransferIncompleteError } from './errors.js';
import type { FileSink, SaveOutcome } from './sinks/file-sink.js';

export type ReceiveCallbacks = Readonly<{
  /** Called with the announced file; resolves with the sink to write into. The sender waits meanwhile. */
  chooseSink: (meta: FileMeta) => Promise<FileSink>;
  onProgress: (receivedBytes: number, totalBytes: number) => void;
}>;

export type ReceivedFile = Readonly<{
  meta: FileMeta;
  outcome: SaveOutcome;
}>;

const sendControl = (channel: RTCDataChannel, message: ReceiverControl): void => channel.send(JSON.stringify(message));

export const receiveFile = (channel: RTCDataChannel, callbacks: ReceiveCallbacks): Promise<ReceivedFile> =>
  new Promise((resolve, reject) => {
    channel.binaryType = 'arraybuffer';
    let meta: FileMeta | null = null;
    let sink: FileSink | null = null;
    let receivedBytes = 0;
    let lastReportedBytes = 0;
    let settled = false;

    const fail = (error: Error): void => {
      if (settled) {
        return;
      }
      settled = true;
      const cleanup = sink === null ? Promise.resolve() : sink.abort(error.message);
      cleanup.then(() => reject(error)).catch(() => reject(error));
    };

    const onMeta = (incoming: FileMeta): void => {
      meta = incoming;
      callbacks
        .chooseSink(incoming)
        .then((chosen) => {
          sink = chosen;
          sendControl(channel, { type: 'accept' });
        })
        .catch((error: unknown) => fail(error instanceof Error ? error : new Error(String(error))));
    };

    const onChunk = (buffer: ArrayBuffer): void => {
      if (meta === null || sink === null) {
        fail(new PeerConnectionError('Received file data before the transfer was accepted'));
        return;
      }
      receivedBytes += buffer.byteLength;
      const total = meta.size;
      sink
        .write(buffer)
        .then(() => {
          if (shouldReportProgress(lastReportedBytes, receivedBytes, PROGRESS_REPORT_INTERVAL_BYTES)) {
            lastReportedBytes = receivedBytes;
            sendControl(channel, { type: 'progress', receivedBytes });
          }
          callbacks.onProgress(receivedBytes, total);
        })
        .catch((error: unknown) => fail(new Error(`Writing to the destination failed: ${String(error)}`)));
    };

    const onDone = (): void => {
      if (meta === null || sink === null) {
        fail(new PeerConnectionError('Received end-of-file before the transfer was accepted'));
        return;
      }
      if (receivedBytes !== meta.size) {
        fail(new TransferIncompleteError(meta.size, receivedBytes));
        return;
      }
      const finished = meta;
      sink
        .close()
        .then((outcome) => {
          settled = true;
          sendControl(channel, { type: 'complete' });
          resolve({ meta: finished, outcome });
        })
        .catch((error: unknown) => fail(new Error(`Finishing the file failed: ${String(error)}`)));
    };

    channel.addEventListener('message', (event: MessageEvent<unknown>) => {
      if (typeof event.data === 'string') {
        const control = parseSenderControl(event.data);
        if (control.type === 'meta') {
          onMeta(control.file);
        } else {
          onDone();
        }
        return;
      }
      if (event.data instanceof ArrayBuffer) {
        onChunk(event.data);
        return;
      }
      fail(new PeerConnectionError(`Unexpected data channel payload type: ${typeof event.data}`));
    });

    channel.addEventListener('close', () => {
      if (!settled) {
        fail(new PeerConnectionError('Data channel closed before the transfer finished'));
      }
    });
  });
